import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { combineLatest, take } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { IAllPoliciesMap } from "@gtmhub/goals/models";
import { PolicyService } from "@gtmhub/goals/services/policy.service";
import { toKeyMap } from "@gtmhub/util";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { ModuleFlag } from "@webapp/feature-toggles/models/feature-module.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { localize } from "@webapp/localization/utils/localization.utils";
import { IDesignScore, IDesignScoreDisplayPolicy, IGoalPoliciesMap, IGoalPolicy } from "@webapp/okrs/goals/models/goal-design-score.models";

@Component({
  selector: "okr-design-score",
  templateUrl: "./okr-design-score.component.html",
  styleUrls: ["./okr-design-score.component.less"],
})
export class OkrDesignScoreComponent implements OnChanges {
  @Input() public goalId: string;
  @Input() public designScore: IDesignScore;
  @Input() public attainmentType: string;
  @Input() public ballCount = 5;
  @Input() public a11yLabelledby: string;

  public totalPoints = 100;
  public offset = 10;
  public ballValue = this.totalPoints / this.ballCount;
  public balls = Array.from({ length: this.ballCount }, (v, i) => i);
  public isPopoverOpen: boolean;

  public policies: IDesignScoreDisplayPolicy[];
  public goalPoliciesMap: IGoalPoliciesMap;
  public allPoliciesMap: IAllPoliciesMap;

  private isModuleTasksEnabled: boolean;
  private isDisableTasksFromDesignScoreEnabled: boolean;
  private isHubTasksEnabled: boolean;

  @ViewChild("okrDesignScore") public okrDesignScore: ElementRef;

  constructor(
    private policyService: PolicyService,
    private featureTogglesFacade: FeatureTogglesFacade,
    private accountService: AccountService,
    private analyticsService: AnalyticsService
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.designScore?.currentValue) {
      return;
    }

    if (!this.isModuleTasksEnabled || !this.isHubTasksEnabled) {
      combineLatest([
        this.featureTogglesFacade.isFeatureAvailable$(ModuleFlag.Tasks),
        this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.DisableTasksFromDesignScore),
      ])
        .pipe(take(1))
        .subscribe(([isModuleTasksEnabled, isDisableTasksFromDesignScoreEnabled]) => {
          this.isModuleTasksEnabled = isModuleTasksEnabled;
          this.isDisableTasksFromDesignScoreEnabled = isDisableTasksFromDesignScoreEnabled;
          this.isHubTasksEnabled = this.accountService.featureAvailable("hub.tasks");
        });
    }

    this.parseGoalPolicies(changes.designScore.currentValue.policies);
  }

  public formatScore(input: number): string {
    return `${input}`;
  }

  public keydownTabOnLastElem(): void {
    this.focusOnTitle();
  }

  public onEnterKeydown(): void {
    if (this.isPopoverOpen === true) {
      this.isPopoverOpen = false;
    } else {
      this.isPopoverOpen = true;

      window.setTimeoutOutsideAngular(() => {
        this.focusOnTitle();
      }, 50);
    }
  }

  private focusOnTitle(): void {
    const popoverElement = document.getElementById("okr-design-popover-title");

    popoverElement?.focus();
  }

  public keydownEscClosePopover($event: KeyboardEvent): void {
    $event.stopPropagation();

    this.closePopover();
  }

  public closePopover(): void {
    this.isPopoverOpen = false;

    // Return focus to component
    this.okrDesignScore.nativeElement.focus();
  }

  public emoticonClick(action: "like" | "dislike"): void {
    this.analyticsService.track("Was this helpful?", { type: "OKR-assistant", goal_id: this.goalId, action });

    this.closePopover();
  }

  private async parseGoalPolicies(goalPolicies: IGoalPolicy[]): Promise<void> {
    if (!this.allPoliciesMap) {
      await this.getAllPolicies();
    }

    this.goalPoliciesMap = toKeyMap("policy", goalPolicies);

    this.policies = [
      {
        shouldRender: true,
        title: localize("title_length_under", { to: this.allPoliciesMap.Name.criteria.range.to }),
        isPassed: !!this.goalPoliciesMap["objectiveTitle"]?.isPassed,
      },
      {
        shouldRender: this.attainmentType === "average_kr",
        title: localize("between_key_results", { from: this.allPoliciesMap.MetricsCount.criteria.range.from, to: this.allPoliciesMap.MetricsCount.criteria.range.to }),
        isPassed: !!this.goalPoliciesMap["metricsCount"]?.isPassed,
      },
      {
        shouldRender: this.attainmentType === "average_children",
        title: localize("between_sub_objectives", {
          from: this.allPoliciesMap.ChildrenCount.criteria.range.from,
          to: this.allPoliciesMap.ChildrenCount.criteria.range.to,
        }),
        isPassed: !!this.goalPoliciesMap["objectiveTitle"]?.isPassed,
      },
      {
        shouldRender: this.attainmentType === "average_kr",
        title: localize("zero_yes_no_key_results"),
        isPassed: !!this.goalPoliciesMap["booleanMetricsCount"]?.isPassed,
      },
      {
        shouldRender: this.isHubTasksEnabled && this.attainmentType === "average_kr" && this.isModuleTasksEnabled && !this.isDisableTasksFromDesignScoreEnabled,
        title: localize("create_tasks_to_help_you_achieve", { from: this.allPoliciesMap.TasksCount.criteria.range.from }),
        isPassed: !!this.goalPoliciesMap["tasksCount"]?.isPassed,
      },
    ];
  }

  private async getAllPolicies(): Promise<void> {
    const allPolicies = await this.policyService.getPolicies();

    this.allPoliciesMap = toKeyMap("fieldName", allPolicies);
  }
}
